import React, { useEffect, useState, useCallback } from 'react';
import {
  Grid, Card, CardContent, CardActionArea, Typography, Box,
  Alert, Chip, LinearProgress, List, ListItem,
  ListItemIcon, ListItemText, Divider, Skeleton, Button,
} from '@mui/material';
import ActivityFeed from '../components/ActivityFeed';
import DescriptionIcon from '@mui/icons-material/Description';
import PublishIcon from '@mui/icons-material/Publish';
import RateReviewIcon from '@mui/icons-material/RateReview';
import AssignmentIcon from '@mui/icons-material/Assignment';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import FolderOpenIcon from '@mui/icons-material/FolderOpen';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import PendingActionsIcon from '@mui/icons-material/PendingActions';
import StorageIcon from '@mui/icons-material/Storage';
import InsertDriveFileIcon from '@mui/icons-material/InsertDriveFile';
import { format } from 'date-fns';
import { useDms } from '../contexts/DmsContext';
import { dmsApi } from '../services/dmsApi';
import useDmsPalette from '../hooks/useDmsPalette';

const VAULT_LABELS = {
  procedures: 'Procedures', legal: 'Legal', technical: 'Technical',
  financial: 'Financial', marketing: 'Marketing', contracts: 'Contracts',
  customer: 'Customer', hr: 'HR', compliance: 'Compliance',
};

const STATUS_COLORS = {
  draft: 'default', review: 'warning', published: 'success', archived: 'error',
};

export default function DmsDashboard({ onNavigate, onDocumentSelect }) {
  const MP = useDmsPalette();
  const STAT_CARDS = [
    { key: 'total', label: 'Total Documents', icon: <DescriptionIcon />, color: MP.blue },
    { key: 'published', label: 'Published', icon: <PublishIcon />, color: MP.green },
    { key: 'review', label: 'In Review', icon: <RateReviewIcon />, color: MP.yellow },
    { key: 'pendingWorkflows', label: 'Pending Workflows', icon: <AssignmentIcon />, color: '#f97316' },
  ];
  const { stats, loadStats } = useDms();
  const [recentDocs, setRecentDocs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [aiInsights, setAiInsights] = useState([]);
  const [favorites] = useState(() => JSON.parse(localStorage.getItem('dms-favorites') || '[]'));

  const loadData = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      await loadStats();
      try {
        const docData = await dmsApi.listDocuments({ limit: 10, sort: '-updatedAt' });
        setRecentDocs(docData.documents || docData || []);
      } catch {
        // stats.recentDocuments will be used as fallback
      }
      dmsApi.getDashboardInsights().then(res => setAiInsights(res.data.insights || [])).catch(() => {});
    } catch (err) {
      setError(err.error || 'Failed to load dashboard data');
    } finally {
      setLoading(false);
    }
  }, [loadStats]);

  useEffect(() => { loadData(); }, [loadData]);

  const getStatValue = (key) => {
    if (!stats) return 0;
    if (key === 'total') return stats.totalDocuments || stats.total || 0;
    if (key === 'published') {
      const entry = (stats.byStatus || []).find(s => s._id === 'published');
      return entry?.count || stats.published || 0;
    }
    if (key === 'review') {
      const entry = (stats.byStatus || []).find(s => s._id === 'review');
      return entry?.count || stats.inReview || 0;
    }
    if (key === 'pendingWorkflows') return stats.pendingWorkflows || 0;
    return 0;
  };

  const storageUsed = stats?.storageUsed || 0;
  const storageLimit = stats?.storageLimit || 5 * 1024 * 1024 * 1024; // 5GB default
  const storagePercent = storageLimit > 0 ? Math.min((storageUsed / storageLimit) * 100, 100) : 0;

  const recent = recentDocs.length > 0 ? recentDocs : (stats?.recentDocuments || []);

  if (loading) {
    return (
      <Box>
        <Skeleton variant="text" width={220} height={36} sx={{ mb: 1 }} />
        <Skeleton variant="text" width={340} height={20} sx={{ mb: 3 }} />
        <Grid container spacing={2} sx={{ mb: 3 }}>
          {[1, 2, 3, 4].map(n => (
            <Grid item xs={6} md={3} key={n}>
              <Skeleton variant="rectangular" height={90} sx={{ borderRadius: 2 }} />
            </Grid>
          ))}
        </Grid>
        <Grid container spacing={3}>
          <Grid item xs={12} md={8}>
            <Skeleton variant="rectangular" height={320} sx={{ borderRadius: 2 }} />
          </Grid>
          <Grid item xs={12} md={4}>
            <Skeleton variant="rectangular" height={200} sx={{ borderRadius: 2, mb: 2 }} />
            <Skeleton variant="rectangular" height={100} sx={{ borderRadius: 2 }} />
          </Grid>
        </Grid>
      </Box>
    );
  }

  return (
    <Box>
      <Typography variant="h5" fontWeight={700} gutterBottom sx={{ fontSize: { xs: '1.2rem', md: '1.5rem' } }}>Document Management</Typography>
      <Typography variant="body2" color="text.secondary" mb={3}>
        SoldiKeeper Enterprise DMS — All business documents in one place
      </Typography>

      {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>{error}</Alert>}

      {/* Stats Cards */}
      <Grid container spacing={2} sx={{ mb: 3 }}>
        {STAT_CARDS.map(card => (
          <Grid item xs={6} md={3} key={card.key}>
            <Card sx={{
              background: 'rgba(255,255,255,0.05)',
              backdropFilter: 'blur(10px)',
              border: '1px solid rgba(255,255,255,0.1)',
              borderRadius: 3,
            }}>
        <CardContent sx={{ p: { xs: 1.5, md: 2 }, '&:last-child': { pb: { xs: 1.5, md: 2 } } }}>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 0.5 }}>
                  <Typography variant="caption" color="text.secondary" fontWeight={500} sx={{ fontSize: { xs: '0.6rem', md: '0.75rem' } }}>
                    {card.label}
                  </Typography>
                  <Box sx={{ color: card.color, display: 'flex' }}>
                    {React.cloneElement(card.icon, { sx: { fontSize: { xs: 16, md: 20 } } })}
                  </Box>
                </Box>
                <Typography variant="h4" fontWeight={700} sx={{ color: card.color, fontSize: { xs: '1.6rem', md: '2.125rem' } }}>
                  {getStatValue(card.key)}
                </Typography>
              </CardContent>
            </Card>
          </Grid>
        ))}
      </Grid>

      <Grid container spacing={3}>
        {/* Recent Documents */}
        <Grid item xs={12} md={8}>
          <Card sx={{ bgcolor: MP.surface, border: `1px solid ${MP.border}` }}>
            <CardContent sx={{ p: 2 }}>
              <Typography variant="subtitle1" fontWeight={600} sx={{ mb: 1.5 }}>
                Recent Documents
              </Typography>

              {recent.length === 0 ? (
                <Box sx={{ textAlign: 'center', py: 6 }}>
                  <InsertDriveFileIcon sx={{ fontSize: 64, color: 'text.secondary', opacity: 0.3 }} />
                  <Typography color="text.secondary" sx={{ mt: 2 }}>
                    No documents yet. Upload your first document to get started.
                  </Typography>
                  <Button variant="contained" sx={{ mt: 2 }} onClick={() => onNavigate && onNavigate('/vault/procedures')}>
                    Upload First Document
                  </Button>
                </Box>
              ) : (
                <List disablePadding>
                  {recent.slice(0, 10).map((doc, idx) => (
                    <React.Fragment key={doc._id}>
                      {idx > 0 && <Divider sx={{ borderColor: MP.border }} />}
                      <ListItem
                        sx={{
                          px: 1, py: 1, borderRadius: 1, cursor: 'pointer',
                          '&:hover': { bgcolor: `${MP.blue}10` },
                        }}
                        onClick={() => onDocumentSelect && onDocumentSelect(doc)}
                      >
                        <ListItemIcon sx={{ minWidth: 36 }}>
                          <InsertDriveFileIcon sx={{ color: MP.blue, fontSize: 20 }} />
                        </ListItemIcon>
                        <ListItemText
                          primary={doc.title}
                          secondary={`${VAULT_LABELS[doc.vault] || doc.vault || '—'} · ${doc.createdBy?.name || 'Unknown'} · ${doc.updatedAt ? format(new Date(doc.updatedAt), 'MMM d, yyyy') : ''}`}
                          primaryTypographyProps={{ fontWeight: 500, fontSize: '0.85rem', noWrap: true }}
                          secondaryTypographyProps={{ fontSize: '0.7rem' }}
                        />
                        <Chip
                          label={doc.status}
                          size="small"
                          color={STATUS_COLORS[doc.status] || 'default'}
                          sx={{ fontSize: '0.65rem', height: 20, textTransform: 'capitalize' }}
                        />
                      </ListItem>
                    </React.Fragment>
                  ))}
                </List>
              )}
            </CardContent>
          </Card>
        </Grid>

        {/* Right Column: Quick Actions + Storage */}
        <Grid item xs={12} md={4}>
          {/* Quick Actions */}
          <Card sx={{ bgcolor: MP.surface, border: `1px solid ${MP.border}`, mb: 2 }}>
            <CardContent sx={{ p: 2 }}>
              <Typography variant="subtitle1" fontWeight={600} sx={{ mb: 1.5 }}>
                Quick Actions
              </Typography>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                <ActionButton
                  icon={<CloudUploadIcon />}
                  label="Upload Document"
                  color={MP.blue}
                  onClick={() => onNavigate && onNavigate('/vault/procedures')}
                />
                <ActionButton
                  icon={<FolderOpenIcon />}
                  label="Browse Vaults"
                  color={MP.green}
                  onClick={() => onNavigate && onNavigate('/vault/procedures')}
                />
                <ActionButton
                  icon={<AutoAwesomeIcon />}
                  label="AI Search"
                  color={MP.yellow}
                  onClick={() => onNavigate && onNavigate('/vault/procedures')}
                />
                <ActionButton
                  icon={<PendingActionsIcon />}
                  label="My Pending Workflows"
                  color="#f97316"
                  onClick={() => onNavigate && onNavigate('/vault/procedures')}
                />
              </Box>
            </CardContent>
          </Card>

          {/* Storage Usage */}
          {storageUsed > 0 && (
            <Card sx={{ bgcolor: MP.surface, border: `1px solid ${MP.border}` }}>
              <CardContent sx={{ p: 2 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5 }}>
                  <StorageIcon sx={{ color: MP.textSecondary, fontSize: 18 }} />
                  <Typography variant="subtitle1" fontWeight={600}>Storage Usage</Typography>
                </Box>
                <LinearProgress
                  variant="determinate"
                  value={storagePercent}
                  sx={{
                    height: 8, borderRadius: 4, mb: 1,
                    bgcolor: MP.deepBlue,
                    '& .MuiLinearProgress-bar': {
                      bgcolor: storagePercent > 80 ? '#f44336' : storagePercent > 60 ? MP.yellow : MP.green,
                      borderRadius: 4,
                    },
                  }}
                />
                <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                  <Typography variant="caption" color="text.secondary">
                    {(storageUsed / (1024 * 1024)).toFixed(1)} MB used
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    {(storageLimit / (1024 * 1024 * 1024)).toFixed(0)} GB limit
                  </Typography>
                </Box>
              </CardContent>
            </Card>
          )}

          {/* Vault Breakdown (compact) */}
          {stats?.byVault && stats.byVault.length > 0 && (
            <Card sx={{ bgcolor: MP.surface, border: `1px solid ${MP.border}`, mt: 2 }}>
              <CardContent sx={{ p: 2 }}>
                <Typography variant="subtitle1" fontWeight={600} sx={{ mb: 1.5 }}>
                  Vault Breakdown
                </Typography>
                {stats.byVault.map(v => (
                  <Box key={v._id} sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 0.8 }}>
                    <Typography variant="body2" sx={{ fontSize: '0.8rem', textTransform: 'capitalize' }}>
                      {VAULT_LABELS[v._id] || v._id}
                    </Typography>
                    <Chip
                      label={v.count}
                      size="small"
                      sx={{ height: 20, fontSize: '0.7rem', bgcolor: `${MP.blue}20`, color: MP.blue }}
                    />
                  </Box>
                ))}
              </CardContent>
            </Card>
          )}
        </Grid>
      </Grid>

      {/* AI Insights */}
      {aiInsights.length > 0 && (
        <Box sx={{ mt: 3 }}>
          <Typography variant="h6" fontWeight={700} sx={{ mb: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
            🧠 AI Insights
          </Typography>
          <Grid container spacing={2}>
            {aiInsights.map((insight, idx) => (
              <Grid item xs={12} sm={6} key={idx}>
                <Alert
                  severity={insight.type === 'action' ? 'warning' : insight.type === 'success' ? 'success' : insight.type === 'warning' ? 'warning' : 'info'}
                  icon={<span style={{ fontSize: '1.2rem' }}>{insight.icon}</span>}
                  sx={{ borderRadius: 2 }}
                >
                  <Typography variant="subtitle2" fontWeight={700}>{insight.title}</Typography>
                  <Typography variant="body2">{insight.message}</Typography>
                </Alert>
              </Grid>
            ))}
          </Grid>
        </Box>
      )}

      {/* Favorites */}
      {favorites.length > 0 && (
        <Box sx={{ mt: 3 }}>
          <Typography variant="h6" fontWeight={700} sx={{ mb: 1, display: 'flex', alignItems: 'center', gap: 1 }}>
            ⭐ Favorites
          </Typography>
          <Typography variant="caption" color="text.secondary">
            {favorites.length} document{favorites.length > 1 ? 's' : ''} favorited — click a star on any document to add it here
          </Typography>
        </Box>
      )}

      {/* Recent Activity */}
      <Box sx={{ mt: 3 }}>
        <Typography variant="h6" fontWeight={700} sx={{ mb: 2 }}>📊 Recent Activity</Typography>
        <Box sx={{
          background: 'rgba(255,255,255,0.03)',
          backdropFilter: 'blur(10px)',
          border: '1px solid rgba(255,255,255,0.08)',
          borderRadius: 3, p: 2
        }}>
          <ActivityFeed maxItems={8} />
        </Box>
      </Box>
    </Box>
  );
}

/* Quick action button component */
function ActionButton({ icon, label, color, onClick }) {
  const MP = useDmsPalette();
  return (
    <Card sx={{ bgcolor: MP.deepBlue, border: `1px solid ${MP.border}`, '&:hover': { borderColor: color } }}>
      <CardActionArea onClick={onClick} sx={{ p: 1.5 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
          <Box sx={{ color, display: 'flex' }}>
            {React.cloneElement(icon, { sx: { fontSize: 20 } })}
          </Box>
          <Typography variant="body2" fontWeight={500}>{label}</Typography>
        </Box>
      </CardActionArea>
    </Card>
  );
}
